import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from '../redux/hooks';
import { RootState } from '../redux/store';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
`;

const GenresList = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: flex-start;
  row-gap: 10px;
`;

const ListItem = styled.div<{ $isHovered: boolean }>`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.$isHovered ? '#f0e6e1' : '#f8f8f8'};
  white-space: nowrap;
`;

const Form = styled.form`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #a13b16;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
`;

const DangerButton = styled(Button)`
  background: #c0392b;
`;

const CancelButton = styled(Button)`
  background: #999;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

interface Genre {
  genre_id: number;
  genre_name: string;
}

const GenresListView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [genres, setGenres] = useState<Genre[]>([]);

  const [selectedGenre, setSelectedGenre] = useState<Genre | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
      fetch(`${process.env.REACT_APP_BASE_API_URL}/genres`)
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          setGenres(data);
        })
        .catch(err => {
          console.error('Failed to fetch genres:', err);
        });
  }, []);

  const handleSelect = useCallback((genre: Genre) => {
    setSelectedGenre(genre);
    setNameInput(genre.genre_name);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!nameInput.trim()) return;

      if (selectedGenre) {
        fetch(
          `${process.env.REACT_APP_BASE_API_URL}/genres/${selectedGenre.genre_id}`,
          {
            method: "PUT", 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ 
              genre_name: nameInput 
            })
          }
        )
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          setGenres(prev =>
            prev.map(g => data.genre_id === g.genre_id ? data : g)
          );
        })
        .catch(err => {
          console.error('Failed to PUT genre:', err);
        });
      } else {
        fetch(
          `${process.env.REACT_APP_BASE_API_URL}/genres`,
          {
            method: "POST", 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ 
              genre_name: nameInput 
            })
          }
        )
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          setGenres(prev => [...prev, data]);
        })
        .catch(err => {
          console.error('Failed to PUT genre:', err);
        });
      }

      setSelectedGenre(null);
      setNameInput('');
    },
    [accessToken, nameInput, selectedGenre]
  );

  const handleDelete = useCallback(() => {
    if (!selectedGenre) return;

    
    fetch(
          `${process.env.REACT_APP_BASE_API_URL}/genres/${selectedGenre.genre_id}`,
          {
            method: "DELETE", 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            }
          }
        )
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          setGenres(prev => prev.filter(g => g !== selectedGenre));

          return res.json();
        })
        .catch(err => {
          console.error('Failed to DELETE genre:', err);
        });

    setSelectedGenre(null);
    setNameInput('');
  }, [accessToken, selectedGenre]);

  const handleCancel = useCallback(() => {
    setSelectedGenre(null);
    setNameInput('');
  }, []);

  return (
    <PageContainer>
      <TopBar>
        <Title>Жанри</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      <FlexRow>
        <GenresList>
          {genres.map(genre => (
            <ListItem 
              key={genre.genre_name}
              $isHovered={hoveredItem === genre.genre_name}
              onClick={() => handleSelect(genre)}
              onMouseEnter={() => setHoveredItem(genre.genre_name)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {genre.genre_name}
            </ListItem>
          ))}
        </GenresList>

        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Назва жанру"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
          />
          <Button type="submit">
            {selectedGenre ? 'Зберегти' : 'Створити'}
          </Button>
          {selectedGenre && (
            <>
              <DangerButton type="button" onClick={handleDelete}>
                Видалити
              </DangerButton>
              <CancelButton type="button" onClick={handleCancel}>
                Відмінити
              </CancelButton>
            </>
          )}
        </Form>
      </FlexRow>
    </PageContainer>
  );
};

export default GenresListView;